"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Statusband } from "./testlauf/Statusband.tsx";
import { useTestlaufStatus } from "./testlauf/use-testlauf-status.ts";
import type {
  StatusbandAktion,
  StatusbandSchrittPunkt,
} from "./testlauf/status-modell.ts";
import {
  clearTestmodusSession,
  readTestmodusSession,
  writeTestmodusSession,
  type TestmodusSession,
} from "../testmodus/session.ts";

const COMPANY_STORAGE = "strategyiq_active_company";
const TESTBED_BASE = "/dashboard/developer";

function applyUiRequiredSession(ev: {
  schrittCode: string;
  prozessCode: string;
  klarname: string;
  nr: number;
  laufId: string;
  probeRunId: string | null;
  resolvedRoute: string;
  returnTo: string;
  unternehmenId: string;
  unternehmenName: string;
  productCompanyId: string | null;
  serverSessionId: string;
  prozessTitel: string;
  gesamtlaufId: string;
}): TestmodusSession {
  const session: TestmodusSession = {
    laufId: ev.laufId,
    schrittCode: ev.schrittCode,
    prozessCode: ev.prozessCode,
    schrittKlarname: ev.klarname,
    schrittNr: ev.nr,
    prozessTitel: ev.prozessTitel,
    unternehmenId: ev.unternehmenId,
    unternehmenName: ev.unternehmenName,
    productCompanyId: ev.productCompanyId,
    returnTo: ev.returnTo,
    gestartetAm: new Date().toISOString(),
    serverSessionId: ev.serverSessionId,
    gesamtlaufId: ev.gesamtlaufId,
    probeRunId: ev.probeRunId,
    resolvedRoute: ev.resolvedRoute,
    zustand: "laeuft",
    awaitingJudgment: false,
  };
  writeTestmodusSession(session);
  if (ev.productCompanyId) {
    try {
      window.localStorage.setItem(COMPANY_STORAGE, ev.productCompanyId);
    } catch {
      /* ignore */
    }
  }
  return session;
}

/** Bindet Scope-Cookie, wenn der Durchlauf einen Scope-Token liefert (A10). */
async function bindeScopeToken(scopeToken: string | undefined): Promise<void> {
  if (!scopeToken) return;
  await fetch("/api/dev/testlauf/starten", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: scopeToken }),
  }).catch(() => null);
}

/**
 * Globales Statusband über der echten Kundenansicht (A6).
 * Sichtbar bei aktivem Scope oder Legacy-Testmodus-Session.
 */
export function TestmodusHost() {
  const router = useRouter();
  const pathname = usePathname();
  const status = useTestlaufStatus();
  const [busy, setBusy] = useState(false);
  const [detailsOffen, setDetailsOffen] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  const navigatingRef = useRef(false);

  const session = status.session;

  const patchSession = useCallback((patch: Partial<TestmodusSession>) => {
    const current = readTestmodusSession();
    if (!current) return;
    const next = { ...current, ...patch };
    writeTestmodusSession(next);
  }, []);

  const stopStream = useCallback(() => {
    esRef.current?.close();
    esRef.current = null;
  }, []);

  const handleDurchlaufEvent = useCallback(
    (
      ev: {
        kind: string;
        schrittCode?: string;
        prozessCode?: string;
        klarname?: string;
        nr?: number;
        laufId?: string;
        probeRunId?: string | null;
        resolvedRoute?: string;
        returnTo?: string;
        unternehmenId?: string;
        unternehmenName?: string;
        productCompanyId?: string | null;
        serverSessionId?: string;
        prozessTitel?: string;
        status?: string;
      },
      gesamtlaufId: string,
    ) => {
      if (ev.kind === "ui_required" && ev.schrittCode && ev.resolvedRoute) {
        const scopeToken =
          "scopeToken" in ev && typeof (ev as { scopeToken?: unknown }).scopeToken === "string"
            ? (ev as { scopeToken: string }).scopeToken
            : undefined;
        void bindeScopeToken(scopeToken).then(() => {
          applyUiRequiredSession({
            schrittCode: ev.schrittCode!,
            prozessCode: ev.prozessCode ?? ev.schrittCode!.split("-")[0] ?? "",
            klarname: ev.klarname ?? ev.schrittCode!,
            nr: ev.nr ?? 1,
            laufId: ev.laufId ?? "",
            probeRunId: ev.probeRunId ?? null,
            resolvedRoute: ev.resolvedRoute!,
            returnTo: ev.returnTo ?? "/dashboard/developer",
            unternehmenId: ev.unternehmenId ?? "",
            unternehmenName: ev.unternehmenName ?? "",
            productCompanyId: ev.productCompanyId ?? null,
            serverSessionId: ev.serverSessionId ?? "",
            prozessTitel: ev.prozessTitel ?? "",
            gesamtlaufId,
          });
          if (!navigatingRef.current && pathname !== ev.resolvedRoute) {
            navigatingRef.current = true;
            window.location.assign(ev.resolvedRoute!);
          }
        });
        return;
      }
      if (ev.kind === "durchlauf_done") {
        stopStream();
        const current = readTestmodusSession();
        const returnTo =
          current?.returnTo ??
          `/dashboard/developer/prozesse/${encodeURIComponent(
            current?.prozessCode ?? "",
          )}`;
        clearTestmodusSession();
        if (!pathname?.startsWith("/dashboard/developer")) {
          router.push(
            returnTo.includes("/prozesse/")
              ? returnTo.split("?")[0] ?? "/dashboard/developer"
              : "/dashboard/developer",
          );
        }
      }
    },
    [pathname, router, stopStream],
  );

  const startStream = useCallback(
    (gesamtlaufId: string) => {
      stopStream();
      const es = new EventSource(
        `/api/dev/prozesse/durchlauf/${encodeURIComponent(gesamtlaufId)}/stream?live=1`,
      );
      esRef.current = es;
      es.onmessage = (msg) => {
        try {
          const ev = JSON.parse(msg.data) as {
            kind: string;
            schrittCode?: string;
            prozessCode?: string;
            klarname?: string;
            nr?: number;
            laufId?: string;
            probeRunId?: string | null;
            resolvedRoute?: string;
            returnTo?: string;
            unternehmenId?: string;
            unternehmenName?: string;
            productCompanyId?: string | null;
            serverSessionId?: string;
            prozessTitel?: string;
            status?: string;
          };
          handleDurchlaufEvent(ev, gesamtlaufId);
        } catch {
          /* ignore */
        }
      };
    },
    [handleDurchlaufEvent, stopStream],
  );

  useEffect(() => {
    if (!session?.gesamtlaufId || session.awaitingJudgment) {
      stopStream();
      return;
    }
    startStream(session.gesamtlaufId);
    return () => stopStream();
  }, [
    session?.gesamtlaufId,
    session?.awaitingJudgment,
    startStream,
    stopStream,
  ]);

  const beendeScope = useCallback(async (ergebnis: "abgebrochen" | "erfolgreich") => {
    await fetch("/api/dev/testlauf/beenden", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ergebnis }),
    }).catch(() => null);
  }, []);

  const onBeurteilen = useCallback(async () => {
    if (!session || busy) return;
    setBusy(true);
    try {
      const fertig =
        status.zustand === "erfolgreich" || status.zustand === "fehlgeschlagen";

      // Lauf nur abschließen, wenn der Schritt wirklich durch ist — sonst
      // weiterlaufen lassen und nur die echte UI verlassen.
      if (fertig && session.laufId) {
        await fetch(
          `/api/dev/schritte/${encodeURIComponent(session.schrittCode)}/testmodus/zurueck`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ laufId: session.laufId }),
          },
        ).catch(() => null);
      }
      if (session.serverSessionId) {
        await fetch("/api/dev/testmodus/leave", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId: session.serverSessionId }),
        }).catch(() => null);
      }

      const baseReturn =
        session.returnTo ||
        `${TESTBED_BASE}/schritte/${encodeURIComponent(session.schrittCode)}`;
      let returnTo = baseReturn;
      if (!fertig) {
        try {
          const u = new URL(baseReturn, window.location.origin);
          u.searchParams.delete("beurteilen");
          returnTo = `${u.pathname}${u.search}${u.hash}`;
        } catch {
          returnTo = `${TESTBED_BASE}/schritte/${encodeURIComponent(session.schrittCode)}`;
        }
      } else {
        try {
          const u = new URL(baseReturn, window.location.origin);
          u.searchParams.set("beurteilen", "1");
          if (session.schrittKlarname) {
            u.searchParams.set("getestet", session.schrittKlarname);
          }
          returnTo = `${u.pathname}${u.search}${u.hash}`;
        } catch {
          /* keep baseReturn */
        }
      }

      writeTestmodusSession({
        ...session,
        awaitingJudgment: fertig,
        zustand: "verlassen",
      });
      stopStream();
      router.push(returnTo);
    } finally {
      setBusy(false);
    }
  }, [busy, router, session, status.zustand, stopStream]);

  const onNaechsterSchritt = useCallback(async () => {
    if (!session?.gesamtlaufId || busy) return;
    setBusy(true);
    navigatingRef.current = false;
    const gesamtlaufId = session.gesamtlaufId;
    try {
      patchSession({ awaitingJudgment: false, zustand: "laeuft" });
      startStream(gesamtlaufId);
      const res = await fetch(
        `/api/dev/prozesse/durchlauf/${encodeURIComponent(gesamtlaufId)}/weiter`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "skip" }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(
          body.message ??
            "Nächster Schritt konnte nicht gestartet werden. Prozessansicht öffnen.",
        );
      }
    } catch {
      const prozessCode = session.prozessCode;
      clearTestmodusSession();
      stopStream();
      router.push(
        prozessCode
          ? `/dashboard/developer/prozesse/${encodeURIComponent(prozessCode)}`
          : "/dashboard/developer",
      );
    } finally {
      setBusy(false);
    }
  }, [busy, patchSession, router, session, startStream, stopStream]);

  const onPausieren = useCallback(async () => {
    if (!session?.gesamtlaufId || busy) return;
    setBusy(true);
    try {
      await fetch(
        `/api/dev/prozesse/durchlauf/${encodeURIComponent(session.gesamtlaufId)}/pause`,
        { method: "POST", credentials: "include" },
      );
      patchSession({ zustand: "pausiert" });
    } finally {
      setBusy(false);
    }
  }, [busy, patchSession, session]);

  const onStarten = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (session?.gesamtlaufId && session.zustand === "pausiert") {
        await fetch(
          `/api/dev/prozesse/durchlauf/${encodeURIComponent(session.gesamtlaufId)}/resume`,
          { method: "POST", credentials: "include" },
        );
        patchSession({ zustand: "laeuft" });
        return;
      }
      if (session?.zustand === "verlassen" && session.serverSessionId) {
        const res = await fetch("/api/dev/testmodus/reenter", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId: session.serverSessionId }),
        });
        if (res.ok) {
          patchSession({ zustand: "laeuft" });
          const target =
            session.resolvedRoute ||
            (session.productCompanyId
              ? `/dashboard/company/twin/explorer`
              : (session.returnTo ?? "/dashboard/developer"));
          router.push(target);
        }
        return;
      }
      // Scope ohne Legacy-Session: zurück zur Prozessansicht zum Starten.
      if (status.aktiv) {
        router.push(TESTBED_BASE);
      }
    } finally {
      setBusy(false);
    }
  }, [busy, patchSession, router, session, status.aktiv]);

  const onAbbrechen = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (session?.gesamtlaufId) {
        await fetch(
          `/api/dev/prozesse/durchlauf/${encodeURIComponent(session.gesamtlaufId)}/stop`,
          { method: "POST", credentials: "include" },
        );
      } else if (session?.laufId) {
        await fetch(
          `/api/dev/schritte/${encodeURIComponent(session.schrittCode)}/abbrechen`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ laufId: session.laufId }),
          },
        );
      }
      if (status.hatScope) {
        await beendeScope("abgebrochen");
      }
      const returnTo = session?.returnTo ?? TESTBED_BASE;
      clearTestmodusSession();
      stopStream();
      router.push(returnTo);
    } finally {
      setBusy(false);
    }
  }, [beendeScope, busy, router, session, status.hatScope, stopStream]);

  const onBeenden = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (status.hatScope) {
        await beendeScope("erfolgreich");
      }
      if (session?.serverSessionId) {
        await fetch("/api/dev/testmodus/leave", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId: session.serverSessionId }),
        }).catch(() => null);
      }
      clearTestmodusSession();
      stopStream();
      router.push(session?.returnTo ?? TESTBED_BASE);
    } finally {
      setBusy(false);
    }
  }, [beendeScope, busy, router, session, status.hatScope, stopStream]);

  const onFertigMelden = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch("/api/dev/testlauf/ereignis", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ art: "manuell", wert: "fertig-gemeldet" }),
      });
      if (res.ok) {
        const data: unknown = await res.json().catch(() => null);
        if (data && typeof data === "object") {
          const o = data as Record<string, unknown>;
          if (o.abschluss === "erfolgreich" || o.abschluss === "fehlgeschlagen") {
            window.dispatchEvent(
              new CustomEvent("dev-testbed-abschluss", {
                detail: {
                  abschluss: o.abschluss,
                  fehlergrund:
                    typeof o.fehlergrund === "string" ? o.fehlergrund : null,
                  dauerMs: typeof o.dauerMs === "number" ? o.dauerMs : null,
                  costUsd: typeof o.costUsd === "number" ? o.costUsd : null,
                },
              }),
            );
          }
        }
      }
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const onWiederholen = useCallback(() => {
    if (!session) {
      router.push(TESTBED_BASE);
      return;
    }
    const code = session.schrittCode;
    const u = session.unternehmenId;
    clearTestmodusSession();
    router.push(
      `${TESTBED_BASE}/schritte/${encodeURIComponent(code)}?unternehmen=${encodeURIComponent(u)}`,
    );
  }, [router, session]);

  const onCursorPrompt = useCallback(() => {
    if (!session) {
      router.push(TESTBED_BASE);
      return;
    }
    router.push(
      `${TESTBED_BASE}/schritte/${encodeURIComponent(session.schrittCode)}#cursor-prompt`,
    );
  }, [router, session]);

  const onSchrittKlick = useCallback(
    (punkt: StatusbandSchrittPunkt) => {
      if (!punkt.klickbar || !punkt.code) return;
      router.push(
        `${TESTBED_BASE}/schritte/${encodeURIComponent(punkt.code)}`,
      );
    },
    [router],
  );

  const aktionen: StatusbandAktion[] = useMemo(() => {
    const z = status.zustand;
    if (z === "bereit") {
      return [
        { id: "starten", label: "Starten", primaer: true, onClick: () => void onStarten() },
        { id: "beenden", label: "Beenden", onClick: () => void onBeenden() },
      ];
    }
    if (z === "laeuft" || z === "wartet_auf_dich") {
      const list: StatusbandAktion[] = [
        {
          id: "beurteilen",
          label: "Beurteilen",
          primaer: true,
          onClick: () => void onBeurteilen(),
        },
      ];
      if (z === "wartet_auf_dich" && status.manuellFertig) {
        list.push({
          id: "fertig",
          label: "Fertig melden",
          onClick: () => void onFertigMelden(),
        });
      }
      list.push({
        id: "abbrechen",
        label: "Stoppen",
        onClick: () => void onAbbrechen(),
      });
      return list.slice(0, 3);
    }
    if (z === "erfolgreich") {
      const list: StatusbandAktion[] = [
        {
          id: "beurteilen",
          label: "Beurteilen",
          primaer: true,
          onClick: () => void onBeurteilen(),
        },
      ];
      if (session?.gesamtlaufId) {
        list.push({
          id: "naechster",
          label: "Nächster Schritt",
          onClick: () => void onNaechsterSchritt(),
        });
      }
      list.push({
        id: "beenden",
        label: "Beenden",
        onClick: () => void onBeenden(),
      });
      return list.slice(0, 3);
    }
    // fehlgeschlagen
    return [
      {
        id: "details",
        label: "Details",
        onClick: () => setDetailsOffen((v) => !v),
      },
      {
        id: "cursor",
        label: "Cursor-Prompt",
        onClick: onCursorPrompt,
      },
      {
        id: "wiederholen",
        label: "Wiederholen",
        primaer: true,
        onClick: onWiederholen,
      },
    ];
  }, [
    onAbbrechen,
    onBeenden,
    onBeurteilen,
    onCursorPrompt,
    onFertigMelden,
    onNaechsterSchritt,
    onStarten,
    onWiederholen,
    session?.gesamtlaufId,
    status.manuellFertig,
    status.zustand,
  ]);

  // Auf der Testmodus-Einstiegsseite rendert die Seite selbst das Band.
  if (!status.sichtbar) return null;
  if (pathname?.startsWith("/dashboard/developer/test/")) return null;
  // Schrittseite zeigt eigene CTA/Status — Band ausblenden, wenn UI verlassen.
  if (
    pathname?.startsWith("/dashboard/developer/schritte/") &&
    (session?.awaitingJudgment || session?.zustand === "verlassen")
  ) {
    return null;
  }

  return (
    <Statusband
      prozessTitel={status.prozessTitel}
      schrittTitel={status.schrittTitel}
      schrittNummer={status.schrittNummer}
      schrittGesamt={status.schrittGesamt}
      unternehmenName={status.unternehmenName}
      zustand={status.zustand}
      zustandText={status.zustandText}
      elapsedSeconds={status.elapsedSeconds}
      verbindungUnterbrochen={status.verbindungUnterbrochen}
      schritte={status.schritte}
      aktionen={aktionen}
      onSchrittKlick={onSchrittKlick}
      fehlerTechnisch={status.fehlerTechnisch}
      detailsOffen={detailsOffen}
      onDetailsToggle={() => setDetailsOffen((v) => !v)}
      disabled={busy}
    />
  );
}
